import { ChangeEvent, FC, useMemo, useState } from "react";
import { Button, Fab, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { AddIcon } from "../../../../assets/icons";
import CheckedIndicator from "../../../../components/common/CheckedIndicator";
import ErrorMessage from "../../../../components/common/ErrorMessage";
import LockedIndicator from "../../../../components/common/LockedIndicator";
import { CustomColumnMenu } from "../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../components/common/table/TableData/TableDataFooter";
import { useCustomTableDataStyles } from "../../../../components/common/table/TableData/useCustomTableDataStyles";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { RoleRegistry } from "../../../../datasources/responses/adminRoles";
import { NonPaginatedResponse } from "../../../../datasources/responses/common";
import { useResources } from "../../../../hooks/useResources";
import { useCurrentTheme } from "../../../../theme/theme";
import CustomNoRowsOverlay from "../../../results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import RoleDetailsDialog from "./RoleDetails";

/**
 * Renders the list of GreenRouter roles.
 * This component is meant to be used in application "admin" section.
 *
 * Important: the roles list is NOT paginated.
 *
 * @constructor
 */
const RolesList: FC = () => {
	const { t } = useTranslation("feature.roles");
	const tableSx = useCustomTableDataStyles();
	const { theme } = useCurrentTheme();
	const {
		data: roles,
		isLoading,
		error,
		reload,
	} = useResources<NonPaginatedResponse<RoleRegistry>>({ route: ApiRoutes.AdminRoles });

	const [roleId, setRoleId] = useState<string | undefined>();

	// gestione selezione ruolo
	const selectRole = (id: string) => setRoleId(id);
	const closeDialog = () => setRoleId(undefined);

	// --- NEW: Search state ---
	const [searchQuery, setSearchQuery] = useState("");

	// --- NEW: Handle search input change ---
	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const textPaletteColor = theme.palette.primary.contrastText;

	// --- NEW: Filter roles based on search query ---
	const filteredRoles = useMemo(() => {
		if (!roles?.items) return [];
		const q = searchQuery.toLowerCase();
		return roles.items.filter((role) => role.description.toLowerCase().includes(q));
	}, [roles, searchQuery]);

	const columns: GridColDef[] = [
		{
			field: "description",
			headerName: t("description"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("description")}
				</Typography>
			),
		},
		{
			field: "enabled",
			headerName: t("enabled"),
			flex: 1,
			renderHeader: (params) => {
				return (
					<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
						{t("enabled")}
					</Typography>
				);
			},
			renderCell: (params) => {
				return (
					<Stack height={"100%"} justifyContent={"center"}>
						<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
							<CheckedIndicator flag={params.field === "enabled" ? true : false} />
						</Typography>
					</Stack>
				);
			},
		},
		{
			field: "protected",
			headerName: t("protected"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("protected")}
				</Typography>
			),
			renderCell: (params) => {
				return (
					<Stack height={"100%"} justifyContent={"center"}>
						<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
							<LockedIndicator flag={params.field === "protected" ? true : false} />
						</Typography>
					</Stack>
				);
			},
		},
	];

	if (error) {
		return <ErrorMessage httpStatusCode={error.httpCode} accept={{ onClick: reload }} />;
	}

	if (isLoading) {
		return <Skeleton variant="rounded" height="280px" width="100%" />;
	}

	return (
		<Stack>
			<Stack sx={{ m: 2, maxWidth: 240 }}>
				<TextField
					label={t("Search")}
					variant="outlined"
					size="small"
					fullWidth
					value={searchQuery}
					onChange={handleSearchChange}
				/>
			</Stack>

			<Stack>
				<DataGrid
					// Rows and Columns
					getRowId={(row) => row.id}
					rows={filteredRoles}
					columns={columns}
					rowHeight={56}
					onRowClick={(row) => selectRole(row.id.toString())}
					hideFooter
					hideFooterPagination
					disableColumnFilter
					disableDensitySelector
					disableColumnSorting
					// Cursor pointer on hover
					style={{ cursor: "pointer" }}
					// Custom components
					slots={{
						noRowsOverlay: () => <CustomNoRowsOverlay message={t("no roles for this research")} />,
						footer: CustomDataGridFooter,
						columnMenu: CustomColumnMenu,
					}}
					slotProps={{
						columnMenu: {
							disableSort: true,
						},
					}}
					// Custom styles
					sx={tableSx}
				/>
			</Stack>

			<Stack width={"140px"} p={2}>
				<Button loading={isLoading} className="confirm" loadingPosition="start" onClick={reload}>
					{t("reload")}
				</Button>
			</Stack>

			{/* TODO: RoleDetailsDialog (like other dialogs) is always mounted, but this may trigger unwanted server requests */}
			<RoleDetailsDialog
				id={roleId}
				isOpen={roleId !== undefined}
				onCancel={closeDialog}
				onSuccess={() => {
					closeDialog();
					reload();
				}}
			/>

			<Fab color="primary" className="add" onClick={() => selectRole("")}>
				<AddIcon />
			</Fab>
		</Stack>
	);
};

export default RolesList;
